import type { IClientType } from '../Types/account';
import type { IClientAccessSlim, IUser } from '../Types/user';
import { USER_ROLE } from '../Types/user';
import { TUTORIAL_AMC_CLIENT_ID, TUTORIAL_SCENARIO } from '../Constants/tutorialScenario';
import { isTutorialFixtureActive } from './tutorialFixtureState';

export type WorkspaceMode = 'platformAdmin' | 'multiClient' | 'singleClient';

export function getWorkspaceStorageKey(userId: string): string {
  return `workspaceClientId:${userId}`;
}

export function getPrimaryClientId(user: IUser): string | null {
  if (user.client_id != null && user.client_id !== '') {
    return String(user.client_id);
  }
  if (user.client?.id != null) {
    return String(user.client.id);
  }
  return null;
}

function clientToAccessSlim(client: NonNullable<IUser['client']>): IClientAccessSlim {
  return {
    id: String(client.id),
    name: client.name,
    domain: client.domain,
  };
}

export function getAccessibleClients(user: IUser): IClientAccessSlim[] {
  const fromApi = user.accessible_clients ?? [];
  if (fromApi.length > 0) {
    // A repeated grant for the same org would otherwise read as multi-client
    // and hand the user an org switcher with nothing else to switch to.
    const seen = new Set<string>();
    return fromApi.filter((client) => {
      const id = String(client.id);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }
  if (user.client) {
    return [clientToAccessSlim(user.client)];
  }
  return [];
}

export function getWorkspaceMode(user: IUser): WorkspaceMode {
  if (user.role === USER_ROLE.ADMIN && user.client == null) {
    return 'platformAdmin';
  }
  const accessible = getAccessibleClients(user);
  if (accessible.length > 1) {
    return 'multiClient';
  }
  return 'singleClient';
}

export function canSwitchWorkspaceClient(user: IUser): boolean {
  const mode = getWorkspaceMode(user);
  return mode === 'platformAdmin' || mode === 'multiClient';
}

export function canSelectOrganizationInSettings(user: IUser): boolean {
  if (user.role === USER_ROLE.ADMIN) return true;
  if (user.role === USER_ROLE.CLIENT_ADMIN && canSwitchWorkspaceClient(user)) return true;
  return false;
}

export function toClientTypeOptions(clients: IClientAccessSlim[]): IClientType[] {
  return clients.map((client) => ({
    id: client.id,
    name: client.name,
    domain: client.domain,
    campaign_description: null,
    desired_job_functions: null,
    desired_job_levels: null,
  }));
}

export function resolveEffectiveClientId(
  selectedId: string | null | undefined,
  user: IUser
): string | null {
  if (selectedId != null && selectedId !== '') {
    return selectedId;
  }
  return getPrimaryClientId(user);
}

export function isClientInWorkspace(
  clientId: string,
  user: IUser,
  allClientsForAdmin?: IClientType[]
): boolean {
  const normalized = String(clientId);
  if (getWorkspaceMode(user) === 'platformAdmin') {
    return (allClientsForAdmin ?? []).some((c) => String(c.id) === normalized);
  }
  return getAccessibleClients(user).some((c) => String(c.id) === normalized);
}

export function readStoredWorkspaceClientId(userId: string): string | null {
  try {
    const stored = sessionStorage.getItem(getWorkspaceStorageKey(userId));
    return stored && stored !== '' ? stored : null;
  } catch {
    return null;
  }
}

export function writeStoredWorkspaceClientId(userId: string, clientId: string | null): void {
  try {
    const key = getWorkspaceStorageKey(userId);
    if (clientId == null || clientId === '') {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, clientId);
    }
  } catch {
    // sessionStorage may be unavailable
  }
}

export function isTutorialWorkspaceClientId(
  clientId: string | null | undefined,
): boolean {
  return clientId != null && String(clientId) === TUTORIAL_AMC_CLIENT_ID;
}

export function getTourClientBackupKey(userId: string): string {
  return `workspaceClientIdBeforeTour:${userId}`;
}

/** `undefined` = no backup stored yet for this tour session. */
export function readTourClientBackup(
  userId: string,
): string | null | undefined {
  try {
    const raw = sessionStorage.getItem(getTourClientBackupKey(userId));
    if (raw === null) return undefined;
    return raw === "" ? null : raw;
  } catch {
    return undefined;
  }
}

export function writeTourClientBackup(
  userId: string,
  clientId: string | null,
): void {
  try {
    sessionStorage.setItem(getTourClientBackupKey(userId), clientId ?? "");
  } catch {
    // sessionStorage may be unavailable
  }
}

export function clearTourClientBackup(userId: string): void {
  try {
    sessionStorage.removeItem(getTourClientBackupKey(userId));
  } catch {
    // sessionStorage may be unavailable
  }
}

/**
 * Pick a client id to restore after the App Guide.
 * Never returns the tutorial Summitlane client id.
 */
export function resolveClientIdForTourRestore(input: {
  backup: string | null | undefined;
  stored: string | null;
  availableClientIds: string[];
}): string | null {
  const candidates = [input.backup, input.stored];
  for (const id of candidates) {
    if (id == null || id === "") continue;
    if (isTutorialWorkspaceClientId(id)) continue;
    if (
      input.availableClientIds.length === 0 ||
      input.availableClientIds.includes(id)
    ) {
      return id;
    }
  }
  return (
    input.availableClientIds.find((id) => !isTutorialWorkspaceClientId(id)) ??
    null
  );
}

/**
 * Capture the pre-tour client once. Ignores the tutorial client id so a remount
 * mid-tour cannot "backup" Summitlane as the restore target.
 */
export function captureClientIdBeforeTour(input: {
  userId: string;
  currentClientId: string | null;
}): string | null {
  const existing = readTourClientBackup(input.userId);
  if (existing !== undefined) {
    return isTutorialWorkspaceClientId(existing) ? null : existing;
  }

  const stored = readStoredWorkspaceClientId(input.userId);
  const preferred = isTutorialWorkspaceClientId(input.currentClientId)
    ? stored
    : input.currentClientId;
  const safe =
    preferred != null && !isTutorialWorkspaceClientId(preferred)
      ? preferred
      : stored != null && !isTutorialWorkspaceClientId(stored)
        ? stored
        : null;

  writeTourClientBackup(input.userId, safe);
  return safe;
}

export function resolveInitialWorkspaceClientId(
  user: IUser,
  storedId: string | null,
  availableClientIds: string[]
): string | null {
  const mode = getWorkspaceMode(user);
  const safeStored =
    storedId && !isTutorialWorkspaceClientId(storedId) ? storedId : null;

  if (mode === 'platformAdmin') {
    if (safeStored && availableClientIds.includes(safeStored)) {
      return safeStored;
    }
    return null;
  }

  if (mode === 'multiClient') {
    if (safeStored && availableClientIds.includes(safeStored)) {
      return safeStored;
    }
    const primary = getPrimaryClientId(user);
    if (primary && availableClientIds.includes(primary)) {
      return primary;
    }
    return (
      availableClientIds.find((id) => !isTutorialWorkspaceClientId(id)) ?? null
    );
  }

  return getPrimaryClientId(user);
}

export function getClientDisplayName(
  clientId: string | null,
  clients: IClientType[],
  user: IUser
): string {
  if (
    isTutorialFixtureActive() &&
    clientId &&
    String(clientId) === TUTORIAL_AMC_CLIENT_ID
  ) {
    return TUTORIAL_SCENARIO.clientLabel;
  }

  if (clientId) {
    const match = clients.find((c) => String(c.id) === String(clientId));
    if (match?.name) return match.name;
    const accessible = getAccessibleClients(user).find((c) => String(c.id) === String(clientId));
    if (accessible?.name) return accessible.name;
  }
  return user.client?.name ?? '';
}
